import { useLanguage } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';
import { capitalizeWords } from '../../utils/textFormatting';

const PageHero = ({ type = 'services' }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const result = useData();
  const isRTL = language === 'ar';
  const isLoading = !result.data;

  // Get header data based on type
  const getHeaderData = () => {
    switch (type) {
      case 'services':
        return result.data?.service_header || {};
      case 'projects':
        return result.data?.project_header || {};
      case 'contact':
        return result.data?.contact_header || {};
      default:
        return {};
    }
  };

  const headerData = getHeaderData();

  return (
    <section id="hero" className="relative hero-section">
      {isLoading ? (
        <LoadingPlaceholder />
      ) : (
        <div className="relative h-[500px] md:h-[600px] w-full">
          {/* Background Image with Media Queries */}
          <div className="absolute inset-0">
            <picture>
              <source
                media="(min-width: 1024px)"
                srcSet={headerData.desktop_image}
              />
              <source
                media="(min-width: 768px)"
                srcSet={headerData.tablet_image || headerData.desktop_image}
              />
              <img
                src={headerData.mobile_image || headerData.desktop_image}
                alt={headerData.title}
                className="object-cover w-full h-full"
              />
            </picture>
          </div>

          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[var(--primary-color)]/75 to-black/80" />
          <div className="absolute inset-0 bg-gradient-to-r to-transparent from-black/20" />
          {/* Content Container */}
          <div className="container relative px-4 mx-auto h-full lg:px-8">
            <div className="flex flex-col justify-center h-full">
              <div className={`w-full text-center ${isRTL ? 'font-arabic' : 'text-left'} space-y-6 md:space-y-8`}>
                <h1
                  className={`text-2xl font-bold leading-tight text-white drop-shadow-lg md:text-3xl lg:text-4xl animate-fade-in ${!isRTL ? '' : ''}`}
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  {headerData.title}
                </h1>

                <div className="mt-8 md:mt-12">
                  <p
                    className={`text-lg leading-relaxed text-gray-200 drop-shadow-md max-w-1xl md:text-xl lg:text-2xl animate-fade-in-delay ${!isRTL ? '' : ''}`}
                    data-aos="fade-up"
                    data-aos-delay="300"
                  >
                    {capitalizeWords(headerData.description)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const LoadingPlaceholder = () => (
  <div className="flex justify-center items-center h-[500px]">
    <div className="px-4 mx-auto space-y-4 w-full max-w-2xl animate-pulse">
      <div className="h-64 bg-gray-300 rounded-lg"></div>
      <div className="w-3/4 h-8 bg-gray-300 rounded-md"></div>
      <div className="w-1/2 h-6 bg-gray-300 rounded-md"></div>
    </div>
  </div>
);

export default PageHero;
